import React, { ReactNode, useContext, useMemo } from 'react';
import upperFirst from 'lodash/upperFirst';
import { useDispatch } from 'react-redux';

import { DataErrorModalProps } from 'ia-react-core/~/components/modal/DataErrorModal';
import { ClientErrors } from 'ia-react-core/~/interfaces/ClientErrors';
import { Method } from 'axios';
import { Response } from 'ia-react-core/~/interfaces/Response';
import useScopedSelector from '../../../hooks/useScopedSelector';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import filterPropsByComponent from '../../../utils/components/#filterPropsByComponent';
import getDataDst from '../../../utils/components/#getDataDst';
import getValuesDst from '../../../utils/components/#getValuesDst';
import DataErrorModal from '../../modal/DataErrorModal';
import Saga from '../../redux/Saga';
import Resource, { ResourceProps } from '../../resource/Resource';

import Form, { FormProps } from '../Form';
import createFormSaga from './CreateForm.saga';
import SchemaContext from '../../schema/#SchemaContext';
import FormMessages from '../#FormMessages';
import RootContext from '../../config/#RootContext';

export interface CreateFormProps<
    Values,
    Data,
    Error = {}
  > extends
  Omit<FormProps<Values>, 'children' | 'method'>,
  Omit<DataErrorModalProps<Error>, 'error' | 'onRetry'>,
  Omit<ResourceProps<Data, Error>, 'children'> {
  children?: ReactNode;
  /** The name of the form, used as the reducer key. If not provided, the reducer key will be "Form${formId}". The HTTP method to use in the creation request. */
  createMethod?: Method;
  /** List of HTTP status codes to consider as client errors. */
  validationFailureStatuses?: number[];
  /** A function that select the controls errors. */
  controlErrorsSelector?: ((response: any) => ClientErrors<Values>);
  /** A function that select the form errors. */
  formErrorsSelector?: ((response: any) => ClientErrors<Values>);
  /** A function that select the controls warnings. */
  controlWarningsSelector?: ((response: any) => ClientErrors<Values>);
  /** A function that select the form errors. */
  formWarningsSelector?: ((response: any) => ClientErrors<Values>);
  /** Hoist errors at the top of the form instead of under each control. */
  hoistErrors?: boolean;
}
const CreateFormDefaultProps = {
  createMethod: 'POST',
  validationFailureStatuses: [400],
  hoistErrors: false,
};

/**
 * @mode developer
 */
const CreateForm = <
  Values extends object,
  Data,
  Error = {},
>({ children, onSuccess, onComplete, ...props }: CreateFormProps<Values, Data, Error>) => {
  const {
    name, createMethod, validationFailureStatuses, maxAttempts, errorMessageTitle,
    controlErrorsSelector, formErrorsSelector, hoistErrors, dataSrc,
  } = props;
  const { schema } = useContext(SchemaContext);
  const capitalizedName = useMemo(() => upperFirst(name), [name]);
  const createResourceName = useMemo(() => (
    schema.extendName('resource', { ...props, name: `create${capitalizedName}` })
  ), [capitalizedName, props, schema]);
  const createResourceDataSrc = dataSrc || getDataDst(props) || getValuesDst(props);

  const error: Response = useScopedSelector(`${createResourceName}.error`);
  const pending: boolean = useScopedSelector(`${createResourceName}.pending`);
  const attempts: number = useScopedSelector(`${createResourceName}.attempts`);

  const dispatch = useDispatch();

  const { root } = useContext(RootContext);

  return (
    <Resource<Data, Error>
      {...filterPropsByComponent(props, Resource<Data, Error>)}
      name={createResourceName}
      method={createMethod}
      dataSrc={createResourceDataSrc}
    >
      {({ resourceId: createResourceId }) => (
        <>
          <Form<Values>
            scrollToFirstError
            hoistErrors={hoistErrors}
            {...filterPropsByComponent(props, Form<Values>)}
          >
            {({ formId, name: formName }) => (
              <>
                <>
                  {!hoistErrors && (
                    <FormMessages
                      formName={formName}
                      messageTitle={errorMessageTitle}
                    />
                  )}
                  {children}
                </>
                <Saga
                  saga={createFormSaga}
                  formId={formId}
                  formName={formName}
                  createResourceId={createResourceId}
                  validationFailureStatuses={validationFailureStatuses}
                  modalName={`${name}ErrorModal${createResourceId}`}
                  root={root}
                  controlErrorsSelector={controlErrorsSelector}
                  formErrorsSelector={formErrorsSelector}
                  onSuccess={onSuccess}
                  onComplete={onComplete}
                />
              </>
            )}
          </Form>
          <DataErrorModal<Error>
            {...filterPropsByComponent(props, DataErrorModal<Error>)}
            name={`${name}ErrorModal${createResourceId}`}
            onRetry={() => dispatch(requestResourceById(createResourceId))}
            pending={pending}
            error={error}
            attempts={attempts}
            maxAttempts={maxAttempts}
          />
        </>
      )}
    </Resource>
  );
};

CreateForm.displayName = 'CreateForm';
CreateForm.defaultProps = CreateFormDefaultProps;

export default CreateForm;
